import { createStoreSlice, type SetField } from 'features/store/zustand';
import { isNavItemActive, type NavItem } from '@/util/navItems';

export type LayoutStore = {
  bannerHeight: number;
  isDrawerOpen: boolean;
  isSidebarExpanded: boolean;
  offsetFromHeader: () => number;
  navItems: NavItem[];
  openNavSections: string[];
  setDefaultOpenNavSection: (defaultOpenNavSectionId: string) => void;
  setField: SetField<LayoutStore>;
  setNavItems: (navItems: NavItem[]) => void;
  toggleDrawer: () => void;
  toggleNavSection: (navSectionId: string) => void;
  toggleSidebar: () => void;
};

const defaultState = {
  bannerHeight: 0,
  isDrawerOpen: false,
  isSidebarExpanded: true,
  navItems: [],
  openNavSections: [],
};

// Determine which nav section should be open by default based on the current URL path
const getOpenNavSections = (navItems: NavItem[]) => {
  const initialOpenSection = navItems.find((item) => isNavItemActive(item, window?.location?.pathname));
  if (initialOpenSection?.children) return [initialOpenSection.id];
  return [];
};

export const createLayoutStoreSlice = createStoreSlice('LayoutStoreData', defaultState, ({ get, set, setField }) => ({
  offsetFromHeader: () => get().bannerHeight,
  setDefaultOpenNavSection: (defaultOpenNavSectionId) => set((s) => ({ openNavSections: [...s.openNavSections, defaultOpenNavSectionId] })),
  setField,
  // Nav items recompute after first paint whenever a flag or the profile resolves; keep the
  // sections the user has opened and only default-open a section that just became the URL's
  // owner (a flag-gated child arriving inside an existing section counts).
  setNavItems: (navItems) =>
    set((s) => {
      const previousDefault = getOpenNavSections(s.navItems);
      const newlyDefault = getOpenNavSections(navItems).filter((id) => !previousDefault.includes(id));
      return { navItems, openNavSections: [...new Set([...s.openNavSections, ...newlyDefault])] };
    }),
  toggleDrawer: () =>
    set((s) => {
      const isDrawerOpen = !s.isDrawerOpen;
      return { isDrawerOpen, openNavSections: isDrawerOpen ? s.openNavSections : getOpenNavSections(s.navItems) };
    }),
  toggleNavSection: (navSectionId: string) =>
    set((s) => {
      const openNavSections = s.openNavSections.includes(navSectionId)
        ? s.openNavSections.filter((id) => id !== navSectionId)
        : [...s.openNavSections, navSectionId];
      // Toggling a section from the collapsed rail expands the sidebar so the section is visible.
      return { isSidebarExpanded: true, openNavSections };
    }),
  toggleSidebar: () =>
    set((s) => {
      const isSidebarExpanded = !s.isSidebarExpanded;
      return { isSidebarExpanded, openNavSections: isSidebarExpanded ? s.openNavSections : getOpenNavSections(s.navItems) };
    }),
}));
